import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

export function CollectiveInvoicePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState({});
  const [dates, setDates] = useState({ invoiceDate: '', dueDate: '' });
  const [error, setError] = useState(null);

  const due = useQuery({ queryKey: ['finance', 'collective-due'], queryFn: () => financeApi.collectiveInvoiceDue().then((r) => r.items) });
  const customers = useQuery({ queryKey: ['sales', 'customers'], queryFn: () => salesApi.listCustomers({ limit: 200 }).then((r) => r.items) });
  const nameOf = useMemo(() => Object.fromEntries((customers.data || []).map((c) => [c.id, c.name])), [customers.data]);

  const rows = due.data || [];
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const selectedCustomer = selectedIds.length ? rows.find((r) => r.id === selectedIds[0])?.customerId : null;

  const toggle = (inv) => setSelected((s) => ({ ...s, [inv.id]: !s[inv.id] }));

  const combine = useMutation({
    mutationFn: () => financeApi.combineInvoices({ customerId: selectedCustomer, invoiceIds: selectedIds, invoiceDate: dates.invoiceDate, dueDate: dates.dueDate }),
    onSuccess: () => { setError(null); setSelected({}); setDates({ invoiceDate: '', dueDate: '' }); queryClient.invalidateQueries({ queryKey: ['finance', 'collective-due'] }); queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] }); },
    onError: (e) => setError(e?.message || t('common.loadError')),
  });

  // Group drafts by customer for the due list.
  const groups = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      if (!m.has(r.customerId)) m.set(r.customerId, []);
      m.get(r.customerId).push(r);
    }
    return [...m.entries()];
  }, [rows]);

  function submit() {
    setError(null);
    if (selectedIds.length < 2) { setError(t('ci.error.min')); return; }
    if (!dates.invoiceDate || !dates.dueDate) { setError(t('ci.error.dates')); return; }
    combine.mutate();
  }

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('ci.title')}</h1>
          <p className="muted">{t('ci.subtitle')}</p>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('ci.metric.drafts')} value={String(rows.length)} />
        <MetricCard label={t('ci.metric.customers')} value={String(groups.length)} />
        <MetricCard label={t('ci.metric.selected')} value={String(selectedIds.length)} trendVariant={selectedIds.length >= 2 ? 'success' : 'neutral'} />
      </div>

      {error && <div className="alert danger" style={{ margin: 'var(--space-2) 0' }}>{error}</div>}

      {due.isLoading ? <p className="muted">…</p> : groups.length === 0 ? <p className="muted">{t('ci.empty')}</p> : (
        groups.map(([customerId, invoices]) => {
          const disabled = selectedCustomer && selectedCustomer !== customerId;
          return (
            <div key={customerId} className="card" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-3)', opacity: disabled ? 0.5 : 1 }}>
              <div className="settings-divider" style={{ marginTop: 0 }}>{nameOf[customerId] || customerId} · {invoices.length} {t('ci.draftsWord')}</div>
              <table className="table">
                <thead><tr><th /><th>{t('ci.col.number')}</th><th>{t('ci.col.date')}</th><th className="num">{t('ci.col.total')}</th></tr></thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td><input type="checkbox" checked={!!selected[inv.id]} disabled={disabled} onChange={() => toggle(inv)} /></td>
                      <td className="mono">{inv.invoiceNumber}</td>
                      <td>{formatDate(inv.invoiceDate)}</td>
                      <td className="num mono">{formatCurrency(inv.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}

      {selectedIds.length >= 2 && (
        <div className="card" style={{ padding: 'var(--space-3)' }}>
          <div className="settings-divider" style={{ marginTop: 0 }}>{t('ci.combineTitle', { n: selectedIds.length })}</div>
          <div className="field-row" style={{ alignItems: 'flex-end' }}>
            <label className="field"><span className="field-label">{t('ci.invoiceDate')}</span>
              <input className="input" type="date" value={dates.invoiceDate} onChange={(e) => setDates((d) => ({ ...d, invoiceDate: e.target.value }))} />
            </label>
            <label className="field"><span className="field-label">{t('ci.dueDate')}</span>
              <input className="input" type="date" value={dates.dueDate} onChange={(e) => setDates((d) => ({ ...d, dueDate: e.target.value }))} />
            </label>
            <Button variant="primary" disabled={combine.isPending} onClick={submit}>{t('ci.action.combine')}</Button>
          </div>
        </div>
      )}
    </section>
  );
}
